<script setup lang="ts">
import { getProductRoute } from "@shopware/helpers";
import { useCmsTranslations } from "@shopware/composables";
import { defu } from "defu";
import { useRouter } from "vue-router";
import type { Schemas } from "#shopware";

const props = withDefaults(
  defineProps<{
    allowRedirect?: boolean;
  }>(),
  {
    allowRedirect: true,
  }
);

type Translations = {
  product: {
    chooseA: string;
  };
};

let translations: Translations = {
  product: {
    chooseA: "Choose a",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const emit = defineEmits<{
  (e: "change", selected: Partial<Schemas["Product"]> | undefined): void;
}>();
const {
  handleChange,
  findVariantForSelectedOptions,
  getSelectedOptions,
  getOptionGroups,
} = useProductConfigurator();
const { apiClient } = useShopwareContext();
const router = useRouter();
const isLoading = ref(false);

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

// Fetch product data
const { product } = useProduct();

// Selected options (from composable)
const selectedOptions = computed(() =>
  Object.fromEntries(
    Object.entries(unref(getSelectedOptions)).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ])
  )
);

// Fetch variants from API
const variants = ref<Schemas["Product"][]>([]);
const fetchVariants = async () => {
  if (!product.value?.parentId) return;
  const response = await apiClient.invoke("readProduct post /product", {
    body: {
      includes: {
        product: [
          "id",
          "options",
          "available",
          "purchaseUnit",
          "unit",
          "calculatedPrices",
          "price",
        ],
      },
      filter: [
        { type: "equals", field: "parentId", value: product.value.parentId },
      ],
      associations: { options: { associations: { group: {} } } },
    },
  });
  variants.value = response?.data?.elements || [];
};
fetchVariants(); // Fetch on component mount

// Compute available combinations
const combinations: ComputedRef<Record<string, any>> = computed(() =>
  variants.value.map((variant) => ({
    id: variant.id,
    availableForSale: variant.available ?? false,
    ...variant?.options?.reduce(
      (acc, option) => ({
        ...acc,
        [option.group.name.toLowerCase()]: option.id,
      }),
      {}
    ),
  }))
);

// Compute disabled options based on valid combinations
const disabledOptions = computed(() => {
  const disabledMap: Record<string, boolean> = {};

  const combinationsNotAvailable = combinations.value.filter(
    (combination: any) => !combination.availableForSale
  );

  combinations.value.forEach((combination: any) => {
    Object.keys(combination).forEach((key) => {
      if (disabledMap[combination[key]] === undefined) {
        // disabledMap[combination[key]] = true;
      }
    });
  });

  return disabledMap;
});

// Function to get variant for a specific option
const getVariantForOption = (groupName: string, optionId: string) => {
  // Make a copy of current selections
  const tempSelection = {
    ...selectedOptions.value,
    [groupName.toLowerCase()]: optionId,
  };

  // Find a matching variant
  return variants.value.find((variant) => {
    if (!variant.options) return false;
    // Return true if this variant matches all our selections
    return variant.options.every((option) => {
      if (!["L", "Kg", "Pcs"].includes(variant.unit?.shortCode)) return false;

      const currentGroupName = option.group.name.toLowerCase();

      // Skip groups that aren't in our selection
      if (tempSelection[currentGroupName] === undefined) return true;
      // Check if option matches selection
      return tempSelection[currentGroupName] === option.id;
    });
  });
};

// Calculate unit price
const calculateUnitPrice = (variant) => {
  if (
    !variant ||
    !variant.calculatedPrices[0]?.unitPrice ||
    !variant.purchaseUnit
  )
    return null;

  // Calculate price per unit
  const unitPrice =
    variant.calculatedPrices[0].unitPrice / variant.purchaseUnit;
  return unitPrice;
};

// Handle option selection
const selectOption = async (groupName: string, optionId: string) => {
  const newSelectedOptions = {
    ...selectedOptions.value,
    [groupName.toLowerCase()]: optionId,
  };

  const optionGroups = Object.keys(newSelectedOptions).filter((key) =>
    combinations.value.some((c) => key in c)
  );

  const combination = combinations.value.find(
    (combination: Record<string, any>) =>
      optionGroups.every((key) => combination[key] === newSelectedOptions[key])
  );

  if (combination) {
    await handleChange(groupName, optionId, onHandleChange);
  } else {
    const alternateSelection = combinations.value.find(
      (combination: Record<string, any>) =>
        optionGroups.some((key) => combination[key] === optionId)
    );
    if (alternateSelection) {
      optionGroups.forEach(async (key, index) => {
        if (key in alternateSelection) {
          newSelectedOptions[key] = alternateSelection[key];
          const cb =
            index === optionGroups.length - 1 ? onHandleChange : () => {};
          await handleChange(key, alternateSelection[key], cb);
        }
      });
    }
  }
};

const onHandleChange = async () => {
  isLoading.value = true;
  const variantFound = await findVariantForSelectedOptions(
    unref(selectedOptions)
  );

  const selectedOptionsVariantPath = getProductRoute(variantFound);
  if (props.allowRedirect && selectedOptionsVariantPath) {
    try {
      router.push(formatLink(selectedOptionsVariantPath));
    } catch (error) {
      console.error("incorrect URL", selectedOptionsVariantPath);
    }
  } else {
    emit("change", variantFound);
  }
  isLoading.value = false;
};
</script>

<template>
  <div class="flex flex-col">
    <div
      v-if="isLoading"
      class="absolute inset-0 flex items-center justify-center z-10 bg-white/75"
    >
      <div
        data-testid="loading"
        class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-gray-500"
      />
    </div>
    <!-- Variant Options -->
    <template v-for="optionGroup in getOptionGroups" :key="optionGroup.id">
      <div v-if="optionGroup.options?.length" class="mt-2">
        <h3 class="text-sm text-gray-500 font-medium">
          {{ optionGroup.name }}
        </h3>
        <fieldset class="mt-4 flex-1">
          <legend class="sr-only">
            {{ translations.product.chooseA }} {{ optionGroup.name }}
          </legend>
          <div class="mb-b">
            <ul
              class="flex flex-wrap gap-x-2.5 gap-y-2.5 font-bold text-skin-secondary [&>li>input:checked+label]:border-skin-theme"
            >
              <li
                v-for="option in optionGroup.options"
                :key="option.id"
                data-testid="product-variant"
                class="relative w-full sm:w-[calc(50%-5px)]"
              >
                <label
                  class="flex flex-row justify-between items-center px-5 bg-skin-icon rounded-full py-2 gap-y-1 border-2 hover:cursor-pointer"
                  :class="{
                    'cursor-default border-skin-theme':
                      selectedOptions[optionGroup.name.toLowerCase()] ===
                      option.id,
                    'border-transparent':
                      !disabledOptions[option.id] &&
                      selectedOptions[optionGroup.name.toLowerCase()] !==
                        option.id,
                    'border-transparent opacity-50 cursor-not-allowed hover:cursor-not-allowed':
                      disabledOptions[option.id],
                  }"
                  :id="`${option.id}-choice-label`"
                  data-testid="product-variant-text"
                  @click="
                    selectOption(optionGroup.name.toLowerCase(), option.id)
                  "
                >
                  <div class="flex items-center gap-x-2.5">
                    <NuxtImg
                      v-if="option.media?.url"
                      :src="option.media?.url"
                      :alt="option.media?.alt || option.name"
                    />
                    <i
                      v-else-if="option.colorHexCode"
                      class="h-2 w-2 rounded-full"
                      :style="`background-color: ${option.colorHexCode}`"
                    ></i>
                    <span class="mt-1">{{ option.name }}</span>
                  </div>
                </label>
              </li>
            </ul>
          </div>
        </fieldset>
      </div>
    </template>
  </div>
</template>
